// Financial Freedom Calculator -- strict TypeScript contracts.
//
// These types mirror the backend Pydantic schemas EXACTLY (see
// backend/app/domain/free_calculators/financial_freedom_calculator/schemas.py).
// Keep the two in sync: TypeScript enforces the network-boundary shape across
// every component in this feature slice.

// NOTE: the project enables `erasableSyntaxOnly`, which forbids TS `enum`
// (a runtime construct). We use the `as const` object + same-named union-type
// pattern instead: the identifier works BOTH as a runtime value
// (`WithdrawalPeriodicity.MONTHLY`) and as a type annotation.
export const Periodicity = {
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    SEMI_ANNUALLY: 'semi-annually',
    ANNUALLY: 'annually',
} as const;
export type Periodicity = (typeof Periodicity)[keyof typeof Periodicity];

export const WithdrawalPeriodicity = {
    MONTHLY: 'monthly',
    ANNUALLY: 'annually',
} as const;
export type WithdrawalPeriodicity =
    (typeof WithdrawalPeriodicity)[keyof typeof WithdrawalPeriodicity];

export interface LumpSum {
    amount: number;
    age_received: number;
}

export interface OtherIncome {
    amount: number;
    periodicity: Periodicity;
    start_age: number;
    end_age: number;
}

// React -> FastAPI payload.
export interface FinancialFreedomInputs {
    current_age: number;
    life_expectancy: number;
    total_savings_today: number;
    stable_annual_income: number;
    annual_savings_rate: number;
    lump_sums: LumpSum[];
    other_incomes: OtherIncome[];
    desired_withdrawal_amount: number;
    withdrawal_periodicity: WithdrawalPeriodicity;
    target_retirement_age?: number | null;
    expected_inflation_rate: number;
    expected_market_return: number;
    market_volatility: number;
}

export interface PortfolioAllocation {
    equities_pct: number;
    bonds_pct: number;
    cash_pct: number;
    reasoning: string;
}

export interface WealthTrajectoryPoint {
    age: number;
    median_portfolio_value: number;
    is_retired: boolean;
}

// FastAPI -> React response (the unified "God Payload").
export interface FinancialFreedomOutputs {
    safest_retirement_age: number;
    safest_retirement_year: number;
    required_savings_for_target_age: number | null;
    required_return_to_retire_today: number;
    recommended_portfolio_today: PortfolioAllocation;
    trajectory: WealthTrajectoryPoint[];
    forge_ultra_hook: string;
}
